import {
  ALLERGENS,
  type Allergen,
  MEALS,
  type Meal,
  type MenuItem,
  addDays,
  avoidedIn,
  isoWeekday,
  todayIso,
  useCurrentContext,
  useMenu,
  useMenuActions,
  weekDays,
  weekStart,
} from '@shkola/api';
import { Apple, Croissant, type LucideIcon, Soup, Utensils } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Spinner } from '../components/ui';
import {
  Block,
  GRAD,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  PAPER,
  PageHead,
  PillIcon,
  grad,
  pillClass,
  ringButton,
} from '../design/forms';
import {
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  PlusIcon,
  WarningCircleIcon,
  XIcon,
} from '../design/icons';
import { dayMonthLong, weekdayShort } from '../student/home/model';

/*
 * «Меню їдальні» in «Форми 2» (28.10): the title on the paper; one white block with the week — its dates
 * for a title and the arrows to go a week back or on — and every day as a row: the day as a small leaf
 * (today on lime), its meals side by side, each with its line icon, the dishes, what they contain; a dish
 * with an allergen the family avoids is on coral with a warning. Beside it the allergens to avoid as
 * chips, kept in this browser only. The administration adds and removes dishes and copies a week.
 */

const AVOID_KEY = 'shkola.avoidAllergens';

const MEAL_ICON: Record<Meal, LucideIcon> = { breakfast: Croissant, lunch: Soup, snack: Apple };

const FIELD =
  'h-10 rounded-[14px] bg-white px-3.5 text-[14px] text-[var(--sh-ink)] outline-none ring-1 ring-[#E4E0D7] ring-inset placeholder:text-[rgb(31_26_53/0.42)] focus:ring-2 focus:ring-[var(--sh-dark)]';

/** Allergens the family avoids: kept in this browser only, never sent anywhere. */
export function useAvoidedAllergens() {
  const [avoid, setAvoid] = useState<Allergen[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(AVOID_KEY) ?? '[]') as Allergen[];
    } catch {
      return [];
    }
  });
  const toggle = (a: Allergen) => {
    const next = avoid.includes(a) ? avoid.filter((x) => x !== a) : [...avoid, a];
    setAvoid(next);
    try {
      localStorage.setItem(AVOID_KEY, JSON.stringify(next));
    } catch {
      // private mode: the choice lasts until the page closes
    }
  };
  return { avoid, toggle };
}

export function Dish({ item, avoid }: { item: MenuItem; avoid: ReadonlyArray<string> }) {
  const { t } = useTranslation();
  const warn = avoidedIn(item, avoid);
  const rest = item.allergens.filter((a) => !warn.includes(a));
  const names = (list: ReadonlyArray<Allergen>) =>
    list.map((a) => t(`canteen.allergens.${a}`).toLowerCase()).join(', ');
  return (
    <div
      className={warn.length ? '-mx-2.5 rounded-[14px] px-2.5 py-2' : ''}
      style={warn.length ? { backgroundImage: grad('coral') } : undefined}
    >
      <p className="text-[15px] leading-[21px]" style={{ color: INK }}>
        {item.title}
        {item.note && <span style={{ color: INK_SOFT }}> · {item.note}</span>}
      </p>
      {warn.length > 0 && (
        <p
          className="mt-1 flex items-center gap-1 text-[12.5px] leading-4 font-medium"
          style={{ color: GRAD.coral.ink }}
        >
          <WarningCircleIcon size={14} weight="fill" aria-hidden className="shrink-0" />
          {t('canteen.warning', { list: names(warn) })}
        </p>
      )}
      {rest.length > 0 && (
        <p className="mt-0.5 text-[12.5px] leading-4" style={{ color: INK_FAINT }}>
          {t('canteen.contains', { list: names(rest) })}
        </p>
      )}
    </div>
  );
}

function AddDish({ schoolId, date, items }: { schoolId: string; date: string; items: MenuItem[] }) {
  const { t } = useTranslation();
  const actions = useMenuActions(schoolId);
  const [open, setOpen] = useState(false);
  const [meal, setMeal] = useState<Meal>('lunch');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [allergens, setAllergens] = useState<Allergen[]>([]);
  if (!open) {
    return (
      <button type="button" className={pillClass('light', 'sm')} onClick={() => setOpen(true)}>
        {t('canteen.add')}
        <PillIcon icon={PlusIcon} size="sm" />
      </button>
    );
  }
  return (
    <form
      className="motion-rise flex flex-col gap-3 rounded-[18px] p-3"
      style={{ backgroundColor: PAPER }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        const position =
          1 + Math.max(0, ...items.filter((i) => i.meal === meal).map((i) => i.position));
        actions.add.mutate(
          { date, meal, title, note, allergens, position },
          {
            onSuccess: () => {
              setTitle('');
              setNote('');
              setAllergens([]);
            },
          },
        );
      }}
    >
      <div className="flex flex-wrap gap-2">
        <Select value={meal} onChange={(e) => setMeal(e.target.value as Meal)}>
          {MEALS.map((m) => (
            <option key={m} value={m}>
              {t(`canteen.meals.${m}`)}
            </option>
          ))}
        </Select>
        <input
          aria-label={t('canteen.dish')}
          placeholder={t('canteen.dish')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={`min-w-0 flex-1 ${FIELD}`}
        />
        <input
          aria-label={t('canteen.note')}
          placeholder={t('canteen.notePlaceholder')}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className={`w-24 ${FIELD}`}
        />
      </div>
      <div className="flex flex-wrap gap-1">
        {ALLERGENS.map((a) => {
          const on = allergens.includes(a);
          return (
            <button
              key={a}
              type="button"
              aria-pressed={on}
              onClick={() =>
                setAllergens(on ? allergens.filter((x) => x !== a) : [...allergens, a])
              }
              className="h-7 rounded-[8px] px-2 text-[12.5px] transition-colors"
              style={
                on
                  ? { backgroundColor: 'var(--sh-dark)', color: '#FFFFFF' }
                  : { backgroundColor: '#FFFFFF', color: INK_SOFT }
              }
            >
              {t(`canteen.allergens.${a}`)}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={actions.add.isPending || !title.trim()}
          className={`${pillClass('dark', 'sm')} disabled:opacity-35`}
        >
          {t('canteen.add')}
          <PillIcon icon={CheckIcon} size="sm" />
        </button>
        <button
          type="button"
          className={`${pillClass('light', 'sm')} pr-3.5`}
          onClick={() => setOpen(false)}
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}

/** «2–6 листопада», «30 жовтня – 3 листопада». */
const rangeOf = (from: string, to: string) =>
  from.slice(0, 7) === to.slice(0, 7)
    ? `${Number(from.slice(8, 10))}–${dayMonthLong(to)}`
    : `${dayMonthLong(from)} – ${dayMonthLong(to)}`;

export function CanteenPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const admin = ctx.kind === 'admin';
  const today = todayIso();
  // on a weekend the coming week is the useful one
  const current = isoWeekday(today) >= 6 ? addDays(weekStart(today), 7) : weekStart(today);
  const [monday, setMonday] = useState(current);
  const days = weekDays(monday, 5);
  const menu = useMenu(ctx.schoolId, addDays(monday, -7), addDays(monday, 6));
  const actions = useMenuActions(ctx.schoolId);
  const { avoid, toggle } = useAvoidedAllergens();
  const [copied, setCopied] = useState<number>();

  const all = menu.data ?? [];
  const thisWeek = all.filter((i) => i.date >= monday);
  const lastWeek = all.filter((i) => i.date < monday);
  const prev = t('canteen.prevWeek').replace(/[←→]/g, '').trim();
  const next = t('canteen.nextWeek').replace(/[←→]/g, '').trim();

  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('canteen.title')} />
      <Block>
        <Head title={t('canteen.avoidTitle')} />
        <div className="flex flex-wrap gap-1.5">
          {ALLERGENS.map((a) => {
            const on = avoid.includes(a);
            return (
              <button
                key={a}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(a)}
                className={`inline-flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[14px] transition-colors ${on ? 'font-medium' : 'hover:bg-[#EFECE5]'}`}
                style={
                  on
                    ? { backgroundImage: grad('coral'), color: GRAD.coral.ink }
                    : { backgroundColor: PAPER, color: INK }
                }
              >
                {on && <CheckIcon size={13} weight="bold" aria-hidden />}
                {t(`canteen.allergens.${a}`)}
              </button>
            );
          })}
        </div>
        <p className="mt-4 text-[13px] leading-[19px]" style={{ color: INK_SOFT }}>
          {t('canteen.avoidHint')} <span style={{ color: INK_FAINT }}>{t('canteen.lawHint')}</span>
        </p>
      </Block>
      <Block>
        <Head title={rangeOf(monday, addDays(monday, 4))}>
          {monday !== current && (
            <button
              type="button"
              className={`${pillClass('light', 'sm')} hidden pr-3.5 sm:inline-flex`}
              onClick={() => setMonday(current)}
            >
              {t('canteen.thisWeek')}
            </button>
          )}
          <button
            type="button"
            aria-label={prev}
            title={prev}
            className={ringButton}
            onClick={() => setMonday(addDays(monday, -7))}
          >
            <CaretLeftIcon size={15} weight="bold" aria-hidden />
          </button>
          <button
            type="button"
            aria-label={next}
            title={next}
            className={ringButton}
            onClick={() => setMonday(addDays(monday, 7))}
          >
            <CaretRightIcon size={15} weight="bold" aria-hidden />
          </button>
        </Head>
        {monday !== current && (
          <button
            type="button"
            className={`${pillClass('light', 'sm')} -mt-2 mb-3 self-start pr-3.5 sm:hidden`}
            onClick={() => setMonday(current)}
          >
            {t('canteen.thisWeek')}
          </button>
        )}

        {admin && thisWeek.length === 0 && lastWeek.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className={`${pillClass('light', 'sm')} pr-3.5 disabled:opacity-50`}
              disabled={actions.copyWeek.isPending}
              onClick={() =>
                actions.copyWeek.mutate(
                  { items: lastWeek, shiftDays: 7 },
                  { onSuccess: () => setCopied(lastWeek.length) },
                )
              }
            >
              {t('canteen.copyWeek')}
            </button>
            {copied !== undefined && (
              <span className="text-[13px]" style={{ color: INK_SOFT }}>
                {t('canteen.copyDone', { count: copied })}
              </span>
            )}
          </div>
        )}

        {menu.isPending ? (
          <Spinner />
        ) : thisWeek.length === 0 && !admin ? (
          <div className="flex flex-col items-center py-12 text-center">
            <span
              className="flex size-20 items-center justify-center rounded-[26px]"
              style={{ backgroundImage: grad('mint'), color: GRAD.mint.ink }}
            >
              <Utensils size={34} strokeWidth={1.6} aria-hidden />
            </span>
            <p
              className="mt-4 max-w-[340px] font-display text-[22px] leading-7 font-light"
              style={{ color: INK }}
            >
              {t('canteen.emptyWeek')}
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-1">
            {days.map((day) => {
              const items = thisWeek.filter((i) => i.date === day);
              const isToday = day === today;
              const meals = MEALS.filter((m) => items.some((i) => i.meal === m));
              return (
                <li
                  key={day}
                  className={`flex gap-3 p-3 sm:gap-4 sm:p-4 ${isToday ? 'rounded-[22px]' : 'border-t border-[#E4E0D7] first:border-t-0'}`}
                  style={isToday ? { backgroundImage: grad('lime') } : undefined}
                >
                  <span className="flex w-14 shrink-0 flex-col items-center">
                    <span
                      className="text-[12.5px] font-medium"
                      style={{ color: isToday ? GRAD.lime.ink : INK_SOFT }}
                    >
                      {isToday ? t('events.today') : weekdayShort(day)}
                    </span>
                    <span
                      className="font-display text-[30px] leading-9 font-light tracking-[-0.03em]"
                      style={{ color: INK }}
                    >
                      {Number(day.slice(8, 10))}
                    </span>
                  </span>
                  <div className="flex min-w-0 flex-1 flex-col gap-3 pt-1">
                    {items.length === 0 ? (
                      <p className="pt-1 text-[14px]" style={{ color: INK_FAINT }}>
                        {t('canteen.empty')}
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 gap-x-8 gap-y-4 sm:grid-cols-2 xl:grid-cols-3">
                        {meals.map((m) => {
                          const Icon = MEAL_ICON[m];
                          return (
                            <div key={m} className="min-w-0">
                              <p
                                className="mb-1.5 flex items-center gap-1.5 text-[12.5px] font-medium"
                                style={{ color: isToday ? GRAD.lime.ink : INK_SOFT }}
                              >
                                <Icon size={15} strokeWidth={1.8} aria-hidden />
                                {t(`canteen.meals.${m}`)}
                              </p>
                              <div className="flex flex-col gap-2">
                                {items
                                  .filter((i) => i.meal === m)
                                  .map((i) => (
                                    <div key={i.id} className="flex items-start gap-2">
                                      <div className="min-w-0 flex-1">
                                        <Dish item={i} avoid={avoid} />
                                      </div>
                                      {admin && (
                                        <button
                                          type="button"
                                          aria-label={`${t('canteen.remove')}: ${i.title}`}
                                          title={t('canteen.remove')}
                                          className="-mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full text-[var(--sh-ink-faint)] transition-colors hover:bg-white hover:text-[#D9471A]"
                                          onClick={() => actions.remove.mutate(i.id)}
                                        >
                                          <XIcon size={12} weight="bold" aria-hidden />
                                        </button>
                                      )}
                                    </div>
                                  ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {admin && (
                      <div>
                        <AddDish schoolId={ctx.schoolId} date={day} items={items} />
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Block>
    </div>
  );
}
